import {
  isPlaybookSectionNode,
  type PlaybookId,
  type PlaybookNode,
} from "@repo/app-types/entities/playbook";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { type FC, useMemo } from "react";
import { Collection, SubmenuTrigger } from "react-aria-components";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import type { PlaybookCondition } from "./playbookCondition.js";
import { PlaybookConditionMenu } from "./playbookConditionMenu.js";

export interface PlaybookConditionsMenuProps {
  playbookId: PlaybookId;
  includeCallsWithoutGrades: boolean;
  onIncludeCallsWithoutGradesChange: (include: boolean) => void;
  onSelect: (condition: PlaybookCondition) => void;
}

interface PlaybookNodeWithDepth {
  id: string;
  node: PlaybookNode;
  depth: number;
}

function flattenPlaybookNodesWithDepth(
  nodes: readonly PlaybookNode[],
  depth = 0,
): PlaybookNodeWithDepth[] {
  return nodes.flatMap((node) => [
    { id: node.id, node, depth },
    ...(isPlaybookSectionNode(node)
      ? flattenPlaybookNodesWithDepth(node.nodes, depth + 1)
      : []),
  ]);
}

export const PlaybookConditionsMenu: FC<PlaybookConditionsMenuProps> = (
  props,
) => {
  const { playbookId } = props;
  const { data: playbook } = usePlaybookByIdQuery(playbookId);

  const nodes = useMemo(
    () =>
      playbook
        ? flattenPlaybookNodesWithDepth(playbook.assessment_tree.nodes)
        : undefined,
    [playbook],
  );

  // TODO: Show loading state
  return (
    <>
      <div className="px-2 py-1.5">
        <Checkbox
          className="cursor-default"
          isChecked={props.includeCallsWithoutGrades}
          onChange={props.onIncludeCallsWithoutGradesChange}
          label="Include calls without grades"
        />
      </div>
      <div className="mx-1 my-1 border-t border-border-light" />
      <SimpleMenu aria-label="Filter by playbook result" className="max-w-xs">
        <Collection items={nodes}>
          {({ node, depth }) => (
            <SubmenuTrigger>
              <SimpleMenuItem indent={depth}>{node.name}</SimpleMenuItem>

              <SimpleMenuPopover>
                <PlaybookConditionMenu
                  playbookId={playbookId}
                  node={node}
                  onSelect={props.onSelect}
                />
              </SimpleMenuPopover>
            </SubmenuTrigger>
          )}
        </Collection>
      </SimpleMenu>
    </>
  );
};
